// FROZEN FILE (Auth). Changed 25 Oct 2026 in the users screen fix (reviewed in chat):
//  - Role list = the roles the caller may give (src/utils/userRoles.js mirrors
//    canGiveRole() in firestore.rules) - incl. Site Engineer; never a role the
//    rules would refuse after the login was already created.
//  - Phone through normaliseIndianMobile() (src/utils/phoneIn.js - the same
//    D-241 logic, moved). Checked only when its field is shown (MED-30).
//  - status follows the Active tick (one source, same class as MED-31).
//  - LOW-14: if the users doc cannot be saved, the just-created login (Auth
//    account) is deleted again through the secondary app - no orphan account.
//  - LOW-15: technician info text (technicians log in by OTP, no password).
//  - Errors are shown directly above the Save button.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, getAuth, signOut } from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { auth, db } from "../../firebase";
import {
  doc, setDoc, addDoc, collection, getDocs, query, where, serverTimestamp,
} from "firebase/firestore";
import { rolesCallerMayGive, canGiveRole } from "../../utils/userRoles";
import { normaliseIndianMobile, normaliseWhatsAppNumber } from "../../utils/phoneIn";

// Creates the login (Auth account) on a SECONDARY Firebase app so the signed-in
// admin stays signed in (D-301), then writes users/{uid} as the admin. If that
// write fails, the new login is deleted again (LOW-14).
async function createStaffUser({ email, password, userDoc }) {
  const secondaryApp  = initializeApp(auth.app.options, "secondary-" + Date.now());
  const secondaryAuth = getAuth(secondaryApp);
  try {
    const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    const uid  = cred.user.uid;
    try {
      await setDoc(doc(db, "users", uid), { uid, ...userDoc });
    } catch (writeErr) {
      try {
        await cred.user.delete();
      } catch (deleteErr) {
        console.error("Add User: could not remove the new login after a failed save:", deleteErr);
        writeErr.orphanEmail = email;
      }
      throw writeErr;
    }
  } finally {
    try { await signOut(secondaryAuth); } catch (_) { /* already signed out */ }
    try { await deleteApp(secondaryApp); } catch (_) { /* already deleted */ }
  }
}

export default function AddUser() {
  const navigate    = useNavigate();
  const currUser    = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId   = currUser?.company_id   || "";
  const companyName = currUser?.company_name || "";
  const callerRole  = currUser?.role || "";
  const roleOptions = rolesCallerMayGive(callerRole);

  const [form, setForm] = useState({
    name: "", email: "", phone: "", whatsapp_phone: "", password: "", role: "technician", active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const isTechnician = form.role === "technician";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError("");
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!companyId) { setError("Company ID missing. Please re-login."); return; }
    if (!canGiveRole(callerRole, form.role)) { setError("You cannot create a user with this role."); return; }

    const name = form.name.trim();
    if (!name) { setError("Name is required."); return; }

    const emailToUse = form.email.trim().toLowerCase();
    if (!isTechnician && !emailToUse)    { setError("Email is required."); return; }
    if (!isTechnician && !form.password) { setError("Password is required."); return; }

    // MED-30: the phone is checked only when its field is shown (technician).
    const phoneCheck = normaliseIndianMobile(isTechnician ? form.phone : "");
    if (isTechnician && phoneCheck.empty) { setError("Phone number is required."); return; }
    if (!phoneCheck.ok) { setError(phoneCheck.error); return; }
    const cleanPhone = phoneCheck.phone;
    // D-297: contact-only WhatsApp number (optional, any role) — never used to log in.
    const waCheck = normaliseWhatsAppNumber(form.whatsapp_phone);
    if (!waCheck.ok) { setError(waCheck.error); return; }
    const status     = form.active ? "active" : "inactive";

    try {
      setLoading(true);

      if (isTechnician) {
        // ── TECHNICIAN — Firestore doc only. No login is created here: the
        //    technician logs in by OTP and the first login claims this doc.
        const existing = await getDocs(query(
          collection(db, "users"),
          where("phone",      "==", cleanPhone),
          where("company_id", "==", companyId)
        ));
        if (!existing.empty) {
          setError("This phone number is already registered in this company.");
          return;
        }

        await addDoc(collection(db, "users"), {
          uid:          "",  // set at the first OTP login
          name,
          email:        "",
          phone:        cleanPhone,
          whatsapp_phone: waCheck.phone,
          role:         "technician",
          active:       form.active,
          status,
          company_id:   companyId,
          company_name: companyName,
          created_at:   serverTimestamp(),
        });

      } else {
        // ── EVERY OTHER ROLE — email + password login
        await createStaffUser({
          email:    emailToUse,
          password: form.password,
          userDoc: {
            name,
            email:        emailToUse,
            phone:        "",
            whatsapp_phone: waCheck.phone,
            role:         form.role,
            active:       form.active,
            status,
            company_id:   companyId,
            company_name: companyName,
            created_at:   serverTimestamp(),
          },
        });
      }

      navigate("/users");
    } catch (err) {
      console.error("Add User Error:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("This email is already registered.");
      } else if (err.orphanEmail) {
        setError(`Not saved. The login for ${err.orphanEmail} was created but could not be removed - ask the SuperAdmin to delete it before trying again.`);
      } else if (err.code === "permission-denied") {
        setError("Not saved - you are not allowed to create this user. Nothing was created.");
      } else {
        setError(`Not saved - ${err.message || "please try again."}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Add User</h1>

      <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-sm text-slate-600">
        🏢 Company: <strong>{companyName || companyId}</strong>
      </div>

      {roleOptions.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
          You cannot add users.
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Full Name *</label>
          <input
            type="text"
            name="name"
            autoComplete="off"
            placeholder="e.g. Rahul Sharma"
            className="border p-2 w-full rounded"
            value={form.name}
            onChange={handleChange}
            required
          />
        </div>

        {/* Role — only the roles this user may give */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Role *</label>
          <select
            name="role"
            className="border p-2 w-full rounded"
            value={form.role}
            onChange={handleChange}
          >
            {roleOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Phone (Technician) or Email (others) */}
        {isTechnician ? (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Mobile Number *</label>
            <input
              type="tel"
              name="phone"
              autoComplete="off"
              placeholder="9876543210"
              className="border p-2 w-full rounded"
              value={form.phone}
              onChange={handleChange}
              required
            />
            <div className="mt-2 bg-blue-50 border border-blue-100 rounded p-2 text-xs text-blue-600">
              ℹ️ The technician logs in with an OTP sent to this number.
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Email Address *</label>
            <input
              type="email"
              name="email"
              autoComplete="off"
              placeholder="user@company.com"
              className="border p-2 w-full rounded"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {/* WhatsApp — contact only, for reminders (D-297); never used to log in */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">WhatsApp number (optional, for reminders)</label>
          <input type="tel" name="whatsapp_phone" autoComplete="off" placeholder="9876543210"
            className="border p-2 w-full rounded" value={form.whatsapp_phone} onChange={handleChange} />
          <p className="text-xs text-slate-400 mt-1">Only for WhatsApp reminders from Team Activity. Never used to log in.</p>
        </div>

        {/* Password — only for non-technicians */}
        {!isTechnician && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Password *</label>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              placeholder="Min 6 characters"
              className="border p-2 w-full rounded"
              value={form.password}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {/* Active */}
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
          Active User
        </label>

        {/* Error next to the button that was pressed */}
        {error && (
          <div role="alert" className="bg-red-100 text-red-600 p-3 rounded text-sm">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`w-full px-4 py-2 rounded text-white font-semibold ${
            loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {loading ? "Creating User..." : "Save User"}
        </button>
      </form>
      )}
    </div>
  );
}